import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Bookmark, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { imgUrl, money } from "@/lib/api";

const FREE_SHIPPING = 150;

export const CartDrawer = () => {
  const {
    cart, cartOpen, setCartOpen, cartSubtotal, updateQuantity, removeFromCart,
    savedItems, saveForLater, moveToBag, removeSaved, itemKey,
  } = useStore();
  const progress = Math.min(100, (cartSubtotal / FREE_SHIPPING) * 100);
  const remaining = FREE_SHIPPING - cartSubtotal;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-black border-white/10 w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="font-display text-white tracking-[0.2em] uppercase text-sm flex items-center gap-2">
            <ShoppingBag size={16} className="text-[#0FB5A3]" /> Your Bag
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 && savedItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
            <p className="text-white/40 text-sm" data-testid="cart-empty-message">Your bag is empty.</p>
            <button onClick={() => setCartOpen(false)} className="btn-757" data-testid="cart-empty-shop-btn">
              <Link to="/shop">Shop Collection</Link>
            </button>
          </div>
        ) : (
          <>
            {cart.length > 0 && (
              <div className="px-6 py-4 border-b border-white/10">
                <p className="text-[11px] tracking-[0.15em] uppercase text-white/50 mb-2">
                  {remaining > 0 ? (
                    <>Add <span className="text-[#0FB5A3]">{money(remaining)}</span> for free shipping</>
                  ) : (
                    <span className="text-[#0FB5A3]">You've unlocked free shipping</span>
                  )}
                </p>
                <div className="h-[3px] bg-white/10">
                  <div className="h-full bg-gradient-to-r from-[#0FB5A3] to-[#6D28D9] transition-[background-color,box-shadow,border-color] duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {cart.map((item) => (
                <div key={itemKey(item)} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)}>
                    <img src={imgUrl(item.image, 200)} alt={item.name} loading="lazy" decoding="async" className="w-20 h-24 object-cover bg-[#0A0A0A]" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-display text-sm text-white truncate">{item.name}</p>
                      <button
                        data-testid={`cart-remove-${item.slug}`}
                        aria-label="Remove item"
                        onClick={() => removeFromCart(item)}
                        className="text-white/30 hover:text-white transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                      Size {item.size}
                      {item.color ? ` · ${item.color}` : ""}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/15">
                        <button
                          data-testid={`cart-qty-minus-${item.slug}`}
                          aria-label="Decrease quantity"
                          onClick={() => updateQuantity(item, item.quantity - 1)}
                          className="p-1.5 text-white/60 hover:text-[#0FB5A3]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-xs text-white" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                        <button
                          data-testid={`cart-qty-plus-${item.slug}`}
                          aria-label="Increase quantity"
                          onClick={() => updateQuantity(item, item.quantity + 1)}
                          className="p-1.5 text-white/60 hover:text-[#0FB5A3]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <p className="text-sm text-white">{money(item.price * item.quantity)}</p>
                    </div>
                    <button
                      data-testid={`cart-save-later-${item.slug}`}
                      onClick={() => saveForLater(item)}
                      className="flex items-center gap-1.5 mt-2.5 text-[10px] tracking-[0.2em] uppercase text-white/35 hover:text-[#C9A24B] transition-colors"
                    >
                      <Bookmark size={11} /> Save for later
                    </button>
                  </div>
                </div>
              ))}

              {savedItems.length > 0 && (
                <div className="border-t border-white/10 pt-5" data-testid="saved-for-later-section">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#C9A24B] mb-4 flex items-center gap-2">
                    <Bookmark size={12} /> Saved for later ({savedItems.length})
                  </p>
                  <div className="space-y-4">
                    {savedItems.map((item) => (
                      <div key={itemKey(item)} className="flex gap-4 items-center" data-testid={`saved-item-${item.slug}`}>
                        <img src={imgUrl(item.image, 120)} alt={item.name} loading="lazy" decoding="async" className="w-14 h-16 object-cover bg-[#0A0A0A] opacity-70" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/70 truncate font-display">{item.name}</p>
                          <p className="text-[11px] text-white/35">Size {item.size} · {money(item.price)}</p>
                          <div className="flex gap-4 mt-1.5">
                            <button
                              data-testid={`saved-move-to-bag-${item.slug}`}
                              onClick={() => moveToBag(item)}
                              className="flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-[#0FB5A3] hover:text-white transition-colors"
                            >
                              <ArrowUpRight size={11} /> Move to bag
                            </button>
                            <button
                              data-testid={`saved-remove-${item.slug}`}
                              onClick={() => removeSaved(item)}
                              className="text-[10px] tracking-[0.15em] uppercase text-white/30 hover:text-white transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5 space-y-4">
                <div className="flex justify-between">
                  <span className="text-xs tracking-[0.2em] uppercase text-white/50">Subtotal</span>
                  <span className="text-base text-white font-display" data-testid="cart-subtotal">{money(cartSubtotal)}</span>
                </div>
                <Link to="/checkout" onClick={() => setCartOpen(false)} className="btn-757 w-full" data-testid="cart-checkout-btn">
                  Secure Checkout
                </Link>
                <p className="text-[10px] text-center text-white/30 tracking-[0.15em] uppercase">
                  Stripe · Apple Pay · Google Pay · Tax & shipping at checkout
                </p>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
